/**
 * Tanzanite — Live Programming console (Round 42 · Workforce v2, Phase 3).
 * The weekly grid (drag a slot to another day), the next seven days with
 * confirmations, the evening coverage timeline, the Captain bench, and the
 * approve-first / auto switch for each outbound channel.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import { CalendarClock, RefreshCw, Plus, X, Pencil, UserPlus, CheckCircle, AlertTriangle, Radio, Users, Send, Search } from 'lucide-react';

const BASE = '/api/admin/workforce/tanzanite';

const STATUS = {
  needs_host: ['No host', 'bg-amber-100 text-amber-800'],
  scheduled: ['Not confirmed', 'bg-gray-100 text-gray-700'],
  confirmed: ['Confirmed', 'bg-emerald-100 text-emerald-800'],
  backup_proposed: ['Backup proposed', 'bg-amber-100 text-amber-800'],
  backup_assigned: ['Backup assigned', 'bg-blue-100 text-blue-800'],
  live: ['Live now', 'bg-red-100 text-red-700'],
  done: ['Done', 'bg-gray-100 text-gray-500'],
  missed: ['No-show', 'bg-red-100 text-red-700'],
};

const time12 = (hhmm) => {
  const [h, m] = String(hhmm || '0:0').split(':').map((x) => parseInt(x, 10) || 0);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const errText = (e, fallback) => e?.response?.data?.detail || fallback;

const MemberPicker = ({ api, onPick, placeholder = 'Search a member by name or @handle' }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const seq = useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRows([]); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`${BASE}/members`, { params: { q: term } });
        if (mine === seq.current) setRows(r.data.members || []);
      } catch (e) { /* the list just stays empty */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q, api]);
  return (
    <div className="relative">
      <div className="flex items-center border rounded-lg px-2 bg-white">
        <Search className="w-3.5 h-3.5 text-gray-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="flex-1 px-2 py-1.5 text-sm outline-none bg-transparent" />
      </div>
      {rows.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {rows.map((m) => (
            <button key={m.id} onClick={() => { onPick(m); setQ(''); setRows([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2">
              {m.picture ? <img src={m.picture} alt="" className="w-6 h-6 rounded-full object-cover" /> : <span className="w-6 h-6 rounded-full bg-gray-200" />}
              <span className="font-medium text-gray-900">{m.name}</span><span className="text-gray-400">@{m.handle}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
      </div>
      {children}
    </div>
  </div>
);

const NUMBERS = [
  ['min_live_tables', 'Minimum live tables (evening)'],
  ['coverage_start_hour', 'Coverage watch starts (hour, ET)'],
  ['coverage_end_hour', 'Coverage watch ends (hour, ET)'],
  ['coverage_auto_max', 'Coverage call: automatic limit (Captains)'],
  ['coverage_cooldown_min', 'Coverage call: minutes between calls'],
  ['coverage_max_per_night', 'Coverage call: most per night'],
  ['open_seat_minutes', 'Open seat: minutes before a push'],
  ['open_seat_auto_max', 'Open seat: automatic limit (members)'],
  ['open_seat_cooldown_min', 'Open seat: minutes between pushes per table'],
  ['open_seat_max_per_table', 'Open seat: most pushes per table'],
  ['per_member_daily_cap', 'Open seat: most per member per day'],
  ['recent_player_days', 'Recent players: days back'],
  ['publish_hour', 'Schedule post hour (ET)'],
];
const SWITCHES = [
  ['reminders_enabled', 'Host reminders'], ['backup_enabled', 'Backup proposals'], ['coverage_enabled', 'Coverage watch'],
  ['open_seat_enabled', 'Open-seat fill'], ['publish_enabled', 'Nightly schedule post'],
];

const TanzanitePage = () => {
  const { api } = useAdmin();
  const [ov, setOv] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState('');
  const [slotForm, setSlotForm] = useState(null);      // {id?, title, day, time, duration_min, game_type, host}
  const [assign, setAssign] = useState(null);          // an upcoming date
  const [dragId, setDragId] = useState(null);
  const [overDay, setOverDay] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`${BASE}/overview`);
      setOv(r.data); setCfg(r.data.config); setDirty(false);
    } catch (e) { toast.error('Could not load Tanzanite'); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const call = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(typeof ok === 'function' ? ok(r) : ok); await load(); return r; }
    catch (e) { toast.error(errText(e, 'That did not work')); return null; }
    finally { setBusy(''); }
  };

  const setMode = (ch) => {
    const next = ch.mode === 'auto' ? 'propose' : 'auto';
    if (next === 'auto' && !window.confirm(`Switch “${ch.label}” to auto?\n\nThey will go out without waiting for approval, inside the limits below. You can switch back at any time.`)) return;
    call(`mode:${ch.key}`, () => api.post(`${BASE}/config`, { [`mode_${ch.key}`]: next }),
      next === 'auto' ? `${ch.label}: auto` : `${ch.label}: approve first`);
  };
  const saveConfig = () => call('save', () => api.post(`${BASE}/config`, cfg), 'Saved');
  const setNum = (k, v) => { setCfg((c) => ({ ...c, [k]: v === '' ? '' : parseInt(v, 10) || 0 })); setDirty(true); };
  const setFlag = (k, v) => { setCfg((c) => ({ ...c, [k]: v })); setDirty(true); };

  const saveSlot = async () => {
    const f = slotForm;
    const body = { title: f.title, series: f.series || '', day: Number(f.day), time: f.time, duration_min: Number(f.duration_min) || 120,
      game_type: f.game_type || '', host_id: f.host ? f.host.id : '' };
    const r = await call('slot', () => (f.id ? api.put(`${BASE}/slots/${f.id}`, body) : api.post(`${BASE}/slots`, body)), f.id ? 'Slot updated' : 'Slot added');
    if (r) setSlotForm(null);
  };
  const removeSlot = (s) => {
    if (!window.confirm(`Take “${s.title}” off the weekly schedule?\n\nIts upcoming dates are cancelled. Past dates stay on record.`)) return;
    call('slot', () => api.delete(`${BASE}/slots/${s.id}`), 'Slot removed');
  };
  const dropOn = (day) => {
    const s = (ov?.slots || []).find((x) => x.id === dragId);
    setOverDay(null); setDragId(null);
    if (!s || s.day === day) return;
    call('slot', () => api.put(`${BASE}/slots/${s.id}`, { day }), `${s.title} moved to ${ov.days[day]}`);
  };
  const editSlot = (s) => setSlotForm({ id: s.id, title: s.title, series: s.series, day: s.day, time: s.time, duration_min: s.duration_min,
    game_type: s.game_type || '', host: s.host_id ? { id: s.host_id, name: s.host_name, handle: s.host_handle } : null });

  const addBench = (m) => call('bench', () => api.post(`${BASE}/bench`, { user_id: m.id, on_call: false }), `@${m.handle} is on the bench`);
  const toggleOnCall = (b) => call('bench', () => api.put(`${BASE}/bench/${b.user_id}`, { on_call: !b.on_call }));
  const removeBench = (b) => call('bench', () => api.delete(`${BASE}/bench/${b.user_id}`), 'Taken off the bench');

  if (!ov || !cfg) return <div className="p-6 text-sm text-gray-400">Loading Tanzanite…</div>;

  const now = Date.now();
  const upcoming = (ov.upcoming || []).filter((o) => new Date(o.end_at).getTime() > now - 3 * 3600 * 1000);
  const maxLive = Math.max(cfg.min_live_tables, ...(ov.coverage || []).map((c) => c.live_tables), 1);

  return (
    <div className="p-6 space-y-5" data-testid="tanzanite-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-indigo-600" /> Tanzanite — Live Programming</h1>
          <p className="text-sm text-gray-500 max-w-3xl">Keeps a table on every night: the weekly schedule, host reminders, a backup from the bench when a host hasn’t confirmed, calls to on-call Captains when the evening is thin, and a push when a live table has a seat open. Everything it sends waits in Decisions until you switch that channel to auto.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => call('publish', () => api.post(`${BASE}/publish-now`), (r) => (r.data.posted ? 'Posted from Table Talk' : r.data.proposed ? 'Waiting in Decisions → Content' : (r.data.reason || 'Nothing to post')))}
            disabled={busy === 'publish'} className="text-xs px-3 py-1.5 border rounded-lg flex items-center gap-1 hover:bg-gray-50 disabled:opacity-50"><Send className="w-3.5 h-3.5" /> Post tomorrow’s schedule</button>
          <button onClick={load} className="text-xs px-3 py-1.5 border rounded-lg flex items-center gap-1 hover:bg-gray-50"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[{ l: 'Live tables now', v: ov.live_tables, c: ov.in_window && ov.live_tables < cfg.min_live_tables ? 'text-red-600' : 'text-gray-800' },
          { l: 'Dates needing attention', v: ov.needs_attention, c: ov.needs_attention ? 'text-amber-600' : 'text-gray-800' },
          { l: 'Waiting in Decisions', v: ov.waiting_in_decisions, c: ov.waiting_in_decisions ? 'text-indigo-600' : 'text-gray-800' },
          { l: 'Captains on the bench', v: `${ov.bench.length} (${ov.bench.filter((b) => b.on_call).length} on call)`, c: 'text-gray-800' }]
          .map((m) => <div key={m.l} className="bg-white border rounded-lg p-3 text-center"><p className={`text-lg font-bold ${m.c}`}>{m.v}</p><p className="text-[10px] text-gray-500">{m.l}</p></div>)}
      </div>

      {/* approve-first / auto, one switch per channel */}
      <div className="bg-white border rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-800">What Tanzanite may send on its own</h3>
        <p className="text-xs text-gray-500 mb-3">Approve first = every message waits in Decisions. Auto = it goes out inside the limits; anything over a limit still waits for you.</p>
        <div className="grid md:grid-cols-2 gap-2">
          {ov.channels.map((ch) => (
            <div key={ch.key} className="flex items-center justify-between border rounded-lg px-3 py-2">
              <span className="text-sm text-gray-800">{ch.label}</span>
              <button onClick={() => setMode(ch)} disabled={busy === `mode:${ch.key}`} data-testid={`tz-mode-${ch.key}`}
                className={`text-xs px-2.5 py-1 rounded-full font-medium ${ch.mode === 'auto' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}>
                {ch.mode === 'auto' ? 'Auto' : 'Approve first'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* week grid */}
      <div className="bg-white border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Weekly schedule</h3>
            <p className="text-xs text-gray-500">Times are Eastern. Drag a slot to another day to move it; dates someone already confirmed stay where they are.</p>
          </div>
          <button onClick={() => setSlotForm({ title: '', series: '', day: 0, time: '20:00', duration_min: 120, game_type: '', host: null })}
            className="text-xs px-3 py-1.5 border rounded-lg flex items-center gap-1 hover:bg-gray-50"><Plus className="w-3.5 h-3.5" /> Add slot</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          {ov.days.map((name, day) => (
            <div key={name} onDragOver={(e) => { e.preventDefault(); setOverDay(day); }} onDragLeave={() => setOverDay((d) => (d === day ? null : d))} onDrop={() => dropOn(day)}
              className={`rounded-lg border p-2 min-h-[120px] ${overDay === day && dragId ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-gray-50'}`}>
              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{name}</p>
              <div className="space-y-1.5">
                {ov.slots.filter((s) => s.day === day).map((s) => (
                  <div key={s.id} draggable onDragStart={() => setDragId(s.id)} onDragEnd={() => { setDragId(null); setOverDay(null); }}
                    className="bg-white border rounded-lg p-2 cursor-grab active:cursor-grabbing shadow-sm">
                    <p className="text-[11px] text-indigo-600 font-semibold">{time12(s.time)} · {s.duration_min} min</p>
                    <p className="text-sm font-medium text-gray-900 leading-tight">{s.title}</p>
                    <p className={`text-xs ${s.host_handle ? 'text-gray-500' : 'text-amber-600'}`}>{s.host_handle ? `@${s.host_handle}` : 'No host yet'}</p>
                    <div className="flex gap-2 mt-1">
                      <button onClick={() => editSlot(s)} className="text-[11px] text-gray-500 hover:text-gray-900 flex items-center gap-0.5"><Pencil className="w-3 h-3" /> Edit</button>
                      <button onClick={() => removeSlot(s)} className="text-[11px] text-gray-400 hover:text-red-600 flex items-center gap-0.5"><X className="w-3 h-3" /> Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* next seven days */}
      <div className="bg-white border rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Next seven days</h3>
        {upcoming.length === 0 && <p className="text-sm text-gray-400">Nothing scheduled.</p>}
        <div className="divide-y">
          {upcoming.map((o) => {
            const [label, cls] = STATUS[o.status] || [o.status, 'bg-gray-100 text-gray-700'];
            const open = !['live', 'done', 'missed'].includes(o.status) && new Date(o.start_at).getTime() > now;
            return (
              <div key={o.id} className="py-2 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{o.title} <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${cls}`}>{label}</span></p>
                  <p className="text-xs text-gray-500">{o.when} · {o.host_handle ? `@${o.host_handle}` : 'no host'}
                    {o.reminders?.t24_sent ? ' · day-before reminder sent' : ''}{o.reminders?.t1_sent ? ' · 1-hour reminder sent' : ''}</p>
                </div>
                {open && (
                  <div className="flex gap-1.5">
                    {o.host_id && o.status !== 'confirmed' && (
                      <button onClick={() => call('occ', () => api.post(`${BASE}/occurrences/${o.id}/confirm`), 'Marked confirmed')}
                        className="text-xs px-2 py-1 border rounded-lg hover:bg-gray-50 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Confirmed</button>
                    )}
                    <button onClick={() => setAssign(o)} className="text-xs px-2 py-1 border rounded-lg hover:bg-gray-50 flex items-center gap-1"><UserPlus className="w-3 h-3" /> {o.host_id ? 'Change host' : 'Assign host'}</button>
                    <button onClick={() => window.confirm(`Cancel ${o.title} on ${o.when}?`) && call('occ', () => api.post(`${BASE}/occurrences/${o.id}/cancel`), 'Date cancelled')}
                      className="text-xs px-2 py-1 border rounded-lg text-gray-500 hover:text-red-600 hover:bg-gray-50">Cancel date</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* coverage timeline */}
        <div className="bg-white border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><Radio className="w-4 h-4 text-red-500" /> Coverage — last 24 hours</h3>
          <p className="text-xs text-gray-500 mb-3">Live tables at each check. Red = inside the evening window and under the minimum of {cfg.min_live_tables}.</p>
          {(ov.coverage || []).length === 0
            ? <p className="text-sm text-gray-400">No checks yet — start Tanzanite on the Workforce page.</p>
            : (
              <div className="flex items-end gap-px h-24 border-b border-gray-200">
                {ov.coverage.map((c) => (
                  <div key={c.at} title={`${new Date(c.at).toLocaleString()} — ${c.live_tables} live`}
                    className={`flex-1 min-w-[2px] rounded-t ${c.in_window && c.live_tables < c.min ? 'bg-red-400' : c.in_window ? 'bg-emerald-400' : 'bg-gray-300'}`}
                    style={{ height: `${Math.max(4, (c.live_tables / maxLive) * 100)}%` }} />
                ))}
              </div>
            )}
          {(ov.open_seats || []).length > 0 && (
            <p className="text-xs text-gray-500 mt-3"><AlertTriangle className="w-3 h-3 inline text-amber-500" /> {ov.open_seats.length} live table{ov.open_seats.length === 1 ? '' : 's'} looking for players right now.</p>
          )}
        </div>

        {/* bench */}
        <div className="bg-white border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><Users className="w-4 h-4 text-indigo-500" /> Captain bench</h3>
          <p className="text-xs text-gray-500 mb-2">Who can step in. On-call Captains get the coverage call and are offered as backups first.</p>
          <MemberPicker api={api} onPick={addBench} placeholder="Add a Captain — search by name or @handle" />
          <div className="divide-y mt-2">
            {ov.bench.length === 0 && <p className="text-sm text-gray-400 py-2">Nobody on the bench yet.</p>}
            {ov.bench.map((b) => (
              <div key={b.user_id} className="py-2 flex items-center justify-between gap-2">
                <p className="text-sm text-gray-900 truncate">{b.name} <span className="text-gray-400">@{b.handle}</span>{b.suspended && <span className="ml-1 text-[10px] text-red-600">suspended</span>}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => toggleOnCall(b)} className={`text-xs px-2 py-0.5 rounded-full ${b.on_call ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>{b.on_call ? 'On call' : 'Off call'}</button>
                  <button onClick={() => removeBench(b)} className="text-gray-400 hover:text-red-600" title="Take off the bench"><X className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* limits */}
      <div className="bg-white border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-800">Limits and switches</h3>
          <button onClick={saveConfig} disabled={!dirty || busy === 'save'} className="text-xs px-3 py-1.5 rounded-lg bg-indigo-600 text-white disabled:opacity-40">Save</button>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 mb-4">
          {SWITCHES.map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5 text-sm text-gray-700"><input type="checkbox" checked={!!cfg[k]} onChange={(e) => setFlag(k, e.target.checked)} /> {l}</label>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {NUMBERS.map(([k, l]) => (
            <label key={k} className="text-xs text-gray-600">{l}
              <input type="number" value={cfg[k]} onChange={(e) => setNum(k, e.target.value)} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm text-gray-900" />
            </label>
          ))}
        </div>
      </div>

      {slotForm && (
        <Modal title={slotForm.id ? 'Edit slot' : 'Add a weekly slot'} onClose={() => setSlotForm(null)}>
          <label className="block text-xs text-gray-600">Title
            <input value={slotForm.title} onChange={(e) => setSlotForm({ ...slotForm, title: e.target.value })} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" placeholder="Friday Night Tables" />
          </label>
          <div className="grid grid-cols-3 gap-2">
            <label className="text-xs text-gray-600">Day
              <select value={slotForm.day} onChange={(e) => setSlotForm({ ...slotForm, day: Number(e.target.value) })} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm">
                {ov.days.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-600">Time (ET)
              <input type="time" value={slotForm.time} onChange={(e) => setSlotForm({ ...slotForm, time: e.target.value })} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
            </label>
            <label className="text-xs text-gray-600">Minutes
              <input type="number" value={slotForm.duration_min} onChange={(e) => setSlotForm({ ...slotForm, duration_min: e.target.value })} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
            </label>
          </div>
          <label className="block text-xs text-gray-600">Game (optional)
            <input value={slotForm.game_type} onChange={(e) => setSlotForm({ ...slotForm, game_type: e.target.value })} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" placeholder="spades" />
          </label>
          <div>
            <p className="text-xs text-gray-600 mb-1">Host</p>
            {slotForm.host
              ? <p className="text-sm flex items-center justify-between border rounded-lg px-2 py-1.5">{slotForm.host.name} <span className="text-gray-400">@{slotForm.host.handle}</span>
                <button onClick={() => setSlotForm({ ...slotForm, host: null })} className="text-gray-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button></p>
              : <MemberPicker api={api} onPick={(m) => setSlotForm({ ...slotForm, host: m })} />}
          </div>
          <button onClick={saveSlot} disabled={busy === 'slot' || slotForm.title.trim().length < 2} className="w-full py-2 rounded-lg bg-indigo-600 text-white text-sm disabled:opacity-40">{slotForm.id ? 'Save' : 'Add slot'}</button>
        </Modal>
      )}

      {assign && (
        <Modal title={`Host for ${assign.title}`} onClose={() => setAssign(null)}>
          <p className="text-xs text-gray-500">{assign.when}. The member you pick is told right away (in-app, push and email with a confirm link). This changes this date only.</p>
          <MemberPicker api={api} onPick={async (m) => {
            const r = await call('occ', () => api.post(`${BASE}/occurrences/${assign.id}/assign`, { user_id: m.id, as_backup: !!assign.host_id }), (x) => x.data.note);
            if (r) setAssign(null);
          }} />
          {ov.bench.filter((b) => !b.suspended).length > 0 && (
            <div>
              <p className="text-xs text-gray-600 mb-1">From the bench</p>
              <div className="flex flex-wrap gap-1.5">
                {ov.bench.filter((b) => !b.suspended).map((b) => (
                  <button key={b.user_id} onClick={async () => {
                    const r = await call('occ', () => api.post(`${BASE}/occurrences/${assign.id}/assign`, { user_id: b.user_id, as_backup: !!assign.host_id }), (x) => x.data.note);
                    if (r) setAssign(null);
                  }} className="text-xs px-2 py-1 border rounded-full hover:bg-gray-50">@{b.handle}{b.on_call ? ' · on call' : ''}</button>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default TanzanitePage;
